import { API_BASE_URL } from "../config/api";
import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import EngineerLayout from "../components/EngineerLayout";
import { downloadReportPdf } from "../utils/reportApi";

const normalizeApprovalStatus = (value = "") => {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "approved") return "Approved";
  if (normalized === "edit access granted") return "Edit Access Granted";
  if (normalized === "re-approved/locked" || normalized === "re-approved") return "Re-Approved/Locked";
  if (normalized === "changes requested" || normalized === "rejected" || normalized === "not approved") {
    return "Changes Requested";
  }
  return "Approval Pending";
};

const getCertificateSummary = (products = []) =>
  (Array.isArray(products) ? products : [])
    .map((product) => product?.certificateNumber || product?.certificate_number || product?.testCertificateNumber || "")
    .filter(Boolean)
    .join(", ");

export default function MyReports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const engineerName = localStorage.getItem("engineerName") || "";
  const engineerLookup =
    localStorage.getItem("engineerId") ||
    localStorage.getItem("engineerEmail") ||
    engineerName;

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const res = await axios.get(API_BASE_URL + "/api/visits", {
          params: { engineer: engineerLookup, excludePdf: 1 }
        });
        const nextReports = (Array.isArray(res.data) ? res.data : []).filter(
          (report) => report?.reportType !== "merged_final"
        );
        nextReports.sort(
          (a, b) =>
            new Date(b.updatedAt || b.createdAt || b.date || 0).getTime() -
            new Date(a.updatedAt || a.createdAt || a.date || 0).getTime()
        );
        setReports(nextReports);
      } catch (error) {
        console.error("Failed to load engineer reports", error);
        setReports([]);
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, [engineerLookup]);

  return (
    <EngineerLayout>
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">My Reports</h1>
            <p className="text-sm text-gray-500 mt-1">
              View your generated report PDFs.
            </p>
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl bg-white shadow">
          {loading ? (
            <div className="p-6 text-gray-500">Loading reports...</div>
          ) : reports.length === 0 ? (
            <div className="p-6 text-gray-500">No reports found</div>
          ) : (
            <>
              <div className="space-y-4 p-4 md:hidden">
                {reports.map((report) => (
                  <article key={report._id} className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-base font-bold text-gray-900">{report.reportId || "-"}</p>
                        <p className="mt-1 text-sm text-gray-500">
                          {report.complaintRefId || report.complaintId || "-"}
                        </p>
                      </div>
                      <span className="inline-flex rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
                        {normalizeApprovalStatus(report.approvalStatus)}
                      </span>
                    </div>

                    <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <MobileInfo label="Customer" value={report.customerName || report.company || "-"} />
                      <MobileInfo
                        label="Date"
                        value={report.date ? new Date(report.date).toLocaleDateString("en-GB") : "-"}
                      />
                      <MobileInfo label="Certificate No" value={getCertificateSummary(report.products) || "-"} />
                    </div>

                    <div className="mt-4 flex flex-wrap gap-2">
                      <button
                        onClick={() => navigate(`/my-reports/${report._id}`)}
                        className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                      >
                        View
                      </button>
                      <button
                        onClick={() => downloadReportPdf(report._id)}
                        className="rounded-xl bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700"
                      >
                        Download
                      </button>
                    </div>
                  </article>
                ))}
              </div>

              <div className="hidden overflow-x-auto md:block">
                <table className="w-full min-w-[960px]">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="p-3 text-left">Report ID</th>
                      <th className="p-3 text-left">Complaint ID</th>
                      <th className="p-3 text-left">Customer</th>
                      <th className="p-3 text-left">Date</th>
                      <th className="p-3 text-left">Certificate No</th>
                      <th className="p-3 text-left">Status</th>
                      <th className="p-3 text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reports.map((report) => (
                      <tr key={report._id} className="border-t">
                        <td className="p-3 font-medium">{report.reportId || "-"}</td>
                        <td className="p-3">{report.complaintRefId || report.complaintId || "-"}</td>
                        <td className="p-3">{report.customerName || report.company || "-"}</td>
                        <td className="p-3">
                          {report.date ? new Date(report.date).toLocaleDateString("en-GB") : "-"}
                        </td>
                        <td className="p-3">{getCertificateSummary(report.products) || "-"}</td>
                        <td className="p-3">{normalizeApprovalStatus(report.approvalStatus)}</td>
                        <td className="p-3">
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => navigate(`/my-reports/${report._id}`)}
                              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded"
                            >
                              View
                            </button>
                            <button
                              onClick={() => downloadReportPdf(report._id)}
                              className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded"
                            >
                              Download
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </EngineerLayout>
  );
}

function MobileInfo({ label, value }) {
  return (
    <div className="rounded-xl bg-gray-50 p-3">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</p>
      <p className="mt-1 text-sm text-gray-900">{value}</p>
    </div>
  );
}


